import isEqual from 'lodash/isEqual';
import { Delta } from 'quill/core';
import getCollapsedDiffViewDelta from './collapsedDelta';
import computeWordLevelDiff from './computeWordLevelDiff';
import createQuillInstance from './createQuillInstance';
export var DiffStatus;
(function (DiffStatus) {
    DiffStatus[DiffStatus["INSERTED"] = 1] = "INSERTED";
    DiffStatus[DiffStatus["DELETED"] = 2] = "DELETED";
    DiffStatus[DiffStatus["RETAINED"] = 3] = "RETAINED";
})(DiffStatus || (DiffStatus = {}));
const DEFAULT_DIFF_CONTEXT_LINES = 2;
// Returns one entry per JS string code unit (same unit that quill-delta's diff() uses).
// Surrogate pairs are kept together on the high-surrogate slot; the low-surrogate slot
// is a null placeholder so array indices stay aligned with code-unit positions.
function flattenDelta(delta) {
    const chars = [];
    for (const op of delta.ops) {
        if (typeof op.insert === 'string') {
            for (let i = 0; i < op.insert.length; i++) {
                const code = op.insert.charCodeAt(i);
                if (code >= 0xD800 && code <= 0xDBFF && i + 1 < op.insert.length) {
                    // High surrogate: store full pair on this slot, push null for the low-surrogate slot
                    chars.push({ value: op.insert[i] + op.insert[i + 1], attributes: op.attributes });
                    chars.push(null);
                    i++;
                }
                else {
                    chars.push({ value: op.insert[i], attributes: op.attributes });
                }
            }
        }
        else if (op.insert) {
            chars.push({ value: op.insert, attributes: op.attributes });
        }
    }
    return chars;
}
function buildDiffViewDelta(oldDelta, diffDelta) {
    const oldChars = flattenDelta(oldDelta);
    const result = new Delta();
    let oldPos = 0;
    for (const op of diffDelta.ops) {
        if (op.retain != null) {
            const len = typeof op.retain === 'number' ? op.retain : 1;
            for (let i = 0; i < len; i++) {
                const entry = oldChars[oldPos + i];
                if (entry) {
                    result.insert(entry.value, {
                        ...entry.attributes,
                        ...op.attributes,
                        diffStatus: DiffStatus.RETAINED,
                    });
                }
            }
            oldPos += len;
        }
        else if (op.delete) {
            for (let i = 0; i < op.delete; i++) {
                const entry = oldChars[oldPos + i];
                if (entry) {
                    result.insert(entry.value, {
                        ...entry.attributes,
                        diffStatus: DiffStatus.DELETED,
                    });
                }
            }
            oldPos += op.delete;
        }
        else if (op.insert) {
            result.insert(op.insert, {
                ...op.attributes,
                diffStatus: DiffStatus.INSERTED,
            });
        }
    }
    for (let i = oldPos; i < oldChars.length; i++) {
        const entry = oldChars[i];
        if (!entry)
            continue;
        result.insert(entry.value, {
            ...entry.attributes,
            diffStatus: DiffStatus.RETAINED,
        });
    }
    return result;
}
function separateInsertedAndDeletedLines(delta) {
    const result = new Delta();
    const ops = delta.ops;
    for (let i = 0; i < ops.length; i++) {
        const op = ops[i];
        const curStatus = op.attributes?.diffStatus;
        if (i > 0 && curStatus) {
            const prevOp = ops[i - 1];
            const prevStatus = prevOp.attributes?.diffStatus;
            const isTransition = (prevStatus === DiffStatus.INSERTED && curStatus === DiffStatus.DELETED) ||
                (prevStatus === DiffStatus.DELETED && curStatus === DiffStatus.INSERTED);
            if (isTransition) {
                const prevText = prevOp.insert;
                const isMultiLine = typeof prevText === 'string' &&
                    prevText.includes('\n') &&
                    !prevText.endsWith('\n');
                if (isMultiLine) {
                    result.insert('\n', { diffStatus: prevStatus });
                }
            }
        }
        result.insert(op.insert, op.attributes);
    }
    return result;
}
function updateDiffStyles(theme, delta) {
    for (let i = 0; i < delta.ops.length; ++i) {
        const op = delta.ops[i];
        if (op.attributes?.diffStatus) {
            if (op.attributes.diffStatus === DiffStatus.INSERTED) {
                if (op.insert && typeof op.insert !== 'string') {
                    op.attributes.padding = `${theme.spacing.standard.md}px`;
                    op.attributes.display = 'block';
                }
                op.attributes.background = theme.palette.background.positive;
            }
            else if (op.attributes.diffStatus === DiffStatus.DELETED) {
                if (op.insert && typeof op.insert !== 'string') {
                    op.attributes.padding = `${theme.spacing.standard.md}px`;
                    op.attributes.display = 'block';
                }
                else {
                    op.attributes.strike = true;
                }
                op.attributes.background = theme.palette.background.negative;
            }
        }
    }
    return delta;
}
export function getDiffViewDelta(theme, quill, oldHTML, newHTML, options = {}) {
    const oldDelta = quill.clipboard.convert({ html: oldHTML });
    const newDelta = quill.clipboard.convert({ html: newHTML });
    const charDiffDelta = oldDelta.diff(newDelta);
    const wordDiffDelta = computeWordLevelDiff(oldDelta, newDelta, charDiffDelta);
    const diffViewDelta = updateDiffStyles(theme, separateInsertedAndDeletedLines(buildDiffViewDelta(oldDelta, wordDiffDelta)));
    if (!options.showOnlyDiff) {
        return diffViewDelta;
    }
    const collapsedDelta = getCollapsedDiffViewDelta(theme, diffViewDelta, options.contextLines ?? DEFAULT_DIFF_CONTEXT_LINES);
    return collapsedDelta.ops.length ? collapsedDelta : diffViewDelta;
}
export function getDiffDelta(oldHTML, newHTML, quill = createQuillInstance(document.createElement('div'), { readOnly: false })) {
    const oldDelta = typeof oldHTML === 'string' ? quill.clipboard.convert({ html: oldHTML }) : new Delta(oldHTML);
    const newDelta = typeof newHTML === 'string' ? quill.clipboard.convert({ html: newHTML }) : new Delta(newHTML);
    return oldDelta.diff(newDelta);
}
export function doesDeltaContainInsOrDel(delta) {
    return delta.ops.some(op => {
        return op.insert || op.delete;
    });
}
export function applyDiffDelta(baseHTML, diffDelta, quill = createQuillInstance(document.createElement('div'), { readOnly: false })) {
    const baseDelta = quill.clipboard.convert({ html: baseHTML });
    const resultDelta = baseDelta.compose(diffDelta);
    quill.setContents(resultDelta);
    return quill.getSemanticHTML();
}
export function areEqualDeltas(a, b) {
    const aOps = typeof a === 'object' ? a.ops : a;
    const bOps = typeof b === 'object' ? b.ops : b;
    return aOps.every((op, index) => {
        const bOp = bOps[index];
        return op.insert === bOp.insert && op.delete === bOp.delete && op.retain === bOp.retain && isEqual(op.attributes, bOp.attributes);
    });
}
